import { format, parseISO } from 'date-fns';

import { exactTime } from '@/util/relativeTime';
import {
  ActivityOrder,
  ShiftDetailActivity,
} from '@/routes/Agency/DashboardV2/api/types';
import Avatar from '@/routes/Agency/DashboardV2/components/Avatar';
import { Sentence } from '@/routes/Agency/DashboardV2/components/ActivityItem';
import useShiftActivity from '@/routes/Agency/DashboardV2/hooks/useShiftActivity';

// The dashboard's tokens (`.dashv2` scope), the GravyWork mark and the sentence
// styles are reused as-is, so the tab and the panel cannot drift apart.
import '@/routes/Agency/DashboardV2/dashboardV2.css';
import './jobActivity.css';

type Props = {
  agencyId: number;
  shiftId: number;
  order: ActivityOrder;
  /** Set when the job has more than one occurrence, to say which one this is. */
  occurrenceLabel?: string | null;
};

/**
 * Who heads an entry (Figma 38490:69618):
 *
 *   automated       → "GravyWork", with the mark
 *   an admin acted  → their FULL name, "(Admin)" — the panel's role label
 *   otherwise       → the worker the event is about
 *
 * The design's "(Shift Supervisor)" is placeholder copy: every person-driven
 * row this log records is an admin's, and the panel already calls them Admin
 * (GRAVY-10350 B2). Two names for one role on two screens would read as two
 * different people.
 */
const headerFor = (item: ShiftDetailActivity) => {
  if (item.automated) return { name: 'GravyWork', role: null, src: null };
  if (item.actor_name) {
    return {
      name: item.actor_full_name || item.actor_name,
      role: 'Admin',
      src: item.actor_avatar_url ?? null,
    };
  }
  return {
    name: item.worker_name || item.title || '',
    role: null,
    src: item.avatar_url ?? null,
  };
};

// "6:57 AM, Jun 6, 2026" — the design's stamp. Absolute, not "2 hrs ago": this
// is the record of the shift, read later, and a relative age goes stale on
// screen. The exact moment, with seconds and zone, stays on hover.
const stampFor = (iso?: string | null) => {
  if (!iso) return '';
  try {
    return format(parseISO(iso), 'h:mm a, MMM d, yyyy');
  } catch {
    return '';
  }
};

/*
 * The server opens an automated sentence with an "(Automated)" actor piece,
 * because a panel row has no name above it to say who acted. Here the entry is
 * already headed "GravyWork" with the mark, so the prefix only repeats it —
 * the frame reads "Surge pay activated. …", not "(Automated) Surge pay …".
 *
 * Only that one piece, and only on an automated row. A person's sentence keeps
 * its actor: "Review U. (Admin) is handling this shift" without it is not a
 * sentence.
 */
const sentenceFor = (item: ShiftDetailActivity) => {
  const segments = item.segments ?? [];
  const [first, ...rest] = segments;
  return item.automated && first?.type === 'actor' ? rest : segments;
};

const Entry = ({ item }: { item: ShiftDetailActivity }) => {
  const who = headerFor(item);
  const segments = sentenceFor(item);

  return (
    <li
      className={`jobact-entry${item.automated ? ' is-automated' : ''}`}
      data-testid="jobActivityEntry"
      data-event-type={item.event_type}
    >
      <div className="jobact-head">
        {item.automated ? (
          <span
            className="dashv2-panel-actbot jobact-mark"
            aria-hidden="true"
          />
        ) : (
          <span className="jobact-avatar">
            <Avatar name={who.name} src={who.src} />
          </span>
        )}
        <div className="jobact-who">
          <span className="jobact-name" data-testid="jobActivityActor">
            <b>{who.name}</b>
            {who.role && <span className="jobact-role"> ({who.role})</span>}
          </span>
          <time
            className="jobact-when"
            dateTime={item.occurred_at ?? undefined}
            title={exactTime(item.occurred_at)}
            data-testid="jobActivityWhen"
          >
            {stampFor(item.occurred_at)}
          </time>
        </div>
      </div>

      <div className="jobact-body">
        <div className="jobact-card" data-testid="jobActivityText">
          {segments.length > 0 ? (
            <Sentence segments={segments} />
          ) : (
            // No sentence for this event type yet — the flat form, which is
            // still every word the server resolved.
            <span className="dashv2-panel-acttext">
              {item.title || item.event_type}
              {item.detail ? ` — ${item.detail}` : ''}
            </span>
          )}
        </div>
      </div>
    </li>
  );
};

/**
 * The Job page's Activity tab (GRAVY-10877, Figma 38490:69618): one shift's
 * history, as a dated record rather than the panel's compact feed.
 *
 * Same endpoint and same sentences as the Dashboard V2 panel's "View all"
 * (`/dashboard_v2/shift_activity`), so an event cannot read one way here and
 * another there. The sort toggle lives in the page's tab strip, as the design
 * places it, and is passed in as `order`.
 */
const JobActivityTab = ({
  agencyId,
  shiftId,
  order,
  occurrenceLabel,
}: Props) => {
  const query = useShiftActivity(agencyId, shiftId, true, order);
  const items = query.data?.pages.flatMap((page) => page.activities) ?? [];

  return (
    <div className="dashv2 jobact" data-testid="jobActivity" data-order={order}>
      {occurrenceLabel && (
        <p className="jobact-occurrence" data-testid="jobActivityOccurrence">
          {occurrenceLabel}
        </p>
      )}

      {query.isPending && <p className="jobact-state">Loading activity…</p>}

      {query.isError && (
        <div className="jobact-state" role="alert">
          Could not load this shift’s activity.{' '}
          <button
            type="button"
            className="jobact-retry"
            onClick={() => query.refetch()}
          >
            Try again
          </button>
        </div>
      )}

      {query.isSuccess && items.length === 0 && (
        <p className="jobact-state" data-testid="jobActivityEmpty">
          No activity on this shift yet.
        </p>
      )}

      {items.length > 0 && (
        <ol className="jobact-list">
          {items.map((item) => (
            <Entry key={item.id} item={item} />
          ))}
        </ol>
      )}

      {query.hasNextPage && (
        <button
          type="button"
          className="jobact-more"
          data-testid="jobActivityMore"
          disabled={query.isFetchingNextPage}
          onClick={() => query.fetchNextPage()}
        >
          {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
};

export default JobActivityTab;
